import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes, { any } from 'prop-types';

import { CheckBox, Select } from '../../Form';
import { automatedDrawTypeDetails, drawAutofillTypeIds, drawEliminationTypeIds, getPlayersCountListToForwardForAutomatedPlayoff } from '../../../utils/eventConstants';
import { required } from '../../../utils/validation';

import styles from './eventDrawCreateEditPopup.scss';
import { showInfoPopup } from '../../../actions/notification-actions';

const getPointsToDisplay = (values) => {
    const drawTypeDetail = automatedDrawTypeDetails.find((detail) => {
        const isPrimaryKeyMatched = parseInt(values[detail.primaryKey]) === parseInt(detail.primaryValue);
        if (isPrimaryKeyMatched) {
            if (detail.needsSecondaryKey) {
                return parseInt(values[detail.secondaryKey]) === parseInt(detail.secondaryValue);
            }

            return true;
        }

        return false;
    });

    return drawTypeDetail?.pointsToDisplay || [];
};

const guaranteedMatchOptions = [
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
    { label: '6', value: 6 },
    { label: '7', value: 7 },
    { label: '8', value: 8 },
];

const GroupDrawFormatFields = (props) => {
    const {
        values,
        isInEditMode,
        onChangeAutoFillType,
        selectedCount,
        onGroupsCountChange,
        playOffDrawOptions,
        onPlayOffDrawCheckChange,
        onGroupPlayersAdvancingChange,
        hideAutofillTypeDescriptions,
        drawDataFromEditSpecificApi,
        resetGroupPlayersAdvancingToPlayoffCount,
    } = props;

    const [isPlayOffCountChangeConfirmed, setIsPlayOffCountChangeConfirmed] = useState(false);
    const dispatch = useDispatch();

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const { drawAutofillTypes } = coreUI.types.tms.draw;

    const pointsToDisplay = getPointsToDisplay(values);

    const guaranteedRROptions = useMemo(() => {
        return [...Array(selectedCount > 0 ? Math.round(selectedCount / 2) - 1 : 0).keys()].map(i => {
            return { label: i + 2, value: i + 2 };
        });
    }, [selectedCount]);

    const isRoundRobin = useMemo(() => {
        return parseInt(values.eliminationTypeId) === drawEliminationTypeIds.ROUND_ROBIN;
    }, [values.eliminationTypeId]);

    const isLevelBasedAutoFillType = useMemo(() => {
        return parseInt(values.autofillTypeId) === drawAutofillTypeIds.LEVEL_BASED;
    }, [values.autofillTypeId]);

    const onChangePlayOffDrawCheck = (playOffDrawOption) => (value) => {
        onPlayOffDrawCheckChange(value, playOffDrawOption);
    };

    const onConfirmGroupPlayersAdvancingChange = useCallback((value) => {
        onGroupPlayersAdvancingChange(value);
    }, [onGroupPlayersAdvancingChange]);

    const onChangePlayersAdvancingToPlayoff = useCallback((value) => {
        const needsConfirmation = isInEditMode &&
            drawDataFromEditSpecificApi?.arePlayersAddedToPlayOff &&
            (parseInt(value) !== parseInt(drawDataFromEditSpecificApi?.groupPlayersAdvancingToPlayoffCount));

        if (needsConfirmation && !isPlayOffCountChangeConfirmed) {
            dispatch(showInfoPopup({
                title: 'Please Confirm Your Playoff Draw Settings Update',
                message: (
                    <ul className="mb0 mt8">
                        <li className="fs14 mb8">
                            Changing the number of players or teams to advance to the playoffs will clear any players or teams that have already been advanced in this draw&apos;s playoff round.
                        </li>
                        <li className="fs14 mb0">
                            The playoff draw will then be re-sized and the new selection of players/teams will be moved to the playoff draw. All standings will be recalculated.
                        </li>
                    </ul>
                ),
                confirmButtonLabel: 'Confirm',
                cancelButtonLabel: 'Cancel',
                showCancelButton: true,
                showConfirmButton: true,
                noIcon: true,
                onCancelClick: () => {
                    resetGroupPlayersAdvancingToPlayoffCount();
                },
                onConfirmClick: () => {
                    setIsPlayOffCountChangeConfirmed(true);
                    onConfirmGroupPlayersAdvancingChange(value);
                },
            }));
        } else {
            onConfirmGroupPlayersAdvancingChange(value);
        }
    }, [isPlayOffCountChangeConfirmed, drawDataFromEditSpecificApi, isInEditMode, onConfirmGroupPlayersAdvancingChange, resetGroupPlayersAdvancingToPlayoffCount]);

    return (
        <>
            {isRoundRobin && (
                <Select
                    id="autofillType"
                    label="Group placement type"
                    field="autofillTypeId"
                    hiddenDefault
                    options={drawAutofillTypes}
                    onChange={onChangeAutoFillType}
                    validate={required}
                    themeV3
                    disabled={isInEditMode}
                />
            )}
            {!hideAutofillTypeDescriptions && pointsToDisplay.length > 0 && (
                <ul className={styles.drawDescription}>
                    {pointsToDisplay.map((value, index) => (
                        <li key={`point-${values.eliminationTypeId}-${index}`}>{value}</li>
                    ))}

                </ul>
            )}
            {isRoundRobin && isLevelBasedAutoFillType && (
                <Select
                    id="matchesGuaranteed"
                    field="matchesGuaranteed"
                    label="# of matches guaranteed"
                    hiddenDefault
                    options={guaranteedMatchOptions}
                    themeV3
                    disabled={isInEditMode}
                />
            )}
            {isRoundRobin && !isLevelBasedAutoFillType && (
                <Select
                    id="groupsCount"
                    field="groupsCount"
                    label="# of groups to create"
                    hiddenDefault
                    options={guaranteedRROptions}
                    onChange={onGroupsCountChange}
                    themeV3
                    disabled={isInEditMode}
                />
            )}
            {playOffDrawOptions?.map((playOffDrawOption) => {
                return (
                    <CheckBox
                        key={`playoff-draw-option-${playOffDrawOption.id}`}
                        id={playOffDrawOption.id}
                        customLabel={`Add ${playOffDrawOption.name}`}
                        field={playOffDrawOption.fieldName}
                        onChange={onChangePlayOffDrawCheck(playOffDrawOption)}
                        disabled={isInEditMode}
                    />
                );
            })}
            {isRoundRobin && !isLevelBasedAutoFillType && values.createPlayoffDraw && (
                <>
                    <Select
                        id="groupPlayersAdvancingToPlayoffCount"
                        field="groupPlayersAdvancingToPlayoffCount"
                        label="# of players / teams to advance from each group"
                        hiddenDefault
                        options={getPlayersCountListToForwardForAutomatedPlayoff(1, selectedCount > 0 ? Math.ceil(selectedCount / parseInt(parseInt(values.groupsCount))) : 2)}
                        onChange={onChangePlayersAdvancingToPlayoff}
                        themeV3
                        disabled={isInEditMode && !drawDataFromEditSpecificApi?.allowPlayOffDrawUpdate}
                    />
                    {values.playersInPlayOffDraw >= 4 && (
                        <CheckBox
                            id="createPlayoffBronzeMatch"
                            customLabel="Add a third place match for the playoff draw"
                            field="createPlayoffBronzeMatch"
                            disabled={isInEditMode && !drawDataFromEditSpecificApi?.allowPlayOffDrawUpdate}
                        />
                    )}
                </>
            )}
        </>
    );
};

GroupDrawFormatFields.propTypes = {
    values: PropTypes.objectOf(any).isRequired,
    isInEditMode: PropTypes.bool,
    onChangeAutoFillType: PropTypes.func,
    selectedCount: PropTypes.number.isRequired,
    onGroupsCountChange: PropTypes.func,
    playOffDrawOptions: PropTypes.arrayOf(any).isRequired,
    onPlayOffDrawCheckChange: PropTypes.func,
    onGroupPlayersAdvancingChange: PropTypes.func,
    resetGroupPlayersAdvancingToPlayoffCount: PropTypes.func,
    hideAutofillTypeDescriptions: PropTypes.bool,
    drawDataFromEditSpecificApi: PropTypes.objectOf(any),
};

GroupDrawFormatFields.defaultProps = {
    isInEditMode: false,
    onGroupPlayersAdvancingChange: () => {},
    onPlayOffDrawCheckChange: () => {},
    onGroupsCountChange: () => {},
    onChangeAutoFillType: () => {},
    resetGroupPlayersAdvancingToPlayoffCount: () => {},
    hideAutofillTypeDescriptions: false,
    drawDataFromEditSpecificApi: null,
};

export default GroupDrawFormatFields;
